import { ChangeEvent, RefObject, useState } from "react";
import { connect, useDispatch } from "react-redux";
import InputBanner from "../InputBanner";
import classNames from "classnames";
import { State } from "redux/store";

import { getNewDatasetId } from "redux/CreateDataset/selectors";
import {
  setAnnotationStatus,
  setConnectStatus,
  setPreviewDataSource,
  setUploadStatus,
  setUploadFinished,
} from "redux/CreateDataset/actions";

import ArrowIcon from "assets/icons/ArrowIcon";
import XIcon from "assets/icons/XIcon";
import styles from "./style.module.scss";
import { DataSources, StepTileProgressStatus } from "types";

const ErrorMessage = ({ kaggleInputError }: { kaggleInputError?: string }) => {
  const dynamicErrorText =
    kaggleInputError || "Error: Please insert valid Kaggle dataset";

  return (
    <span className={styles.errorMessage}>
      <XIcon />
      {dynamicErrorText}
    </span>
  );
};

const DefaultMessage = () => {
  return (
    <span>
      Enter a Kaggle dataset in OWNER/DATASET format or full Kaggle URL
    </span>
  );
};

const KaggleBannerMessage = ({
  kaggleInputError,
}: KaggleBannerMessageProps) => {
  const renderMessage = () => {
    if (kaggleInputError) {
      return <ErrorMessage kaggleInputError={kaggleInputError} />;
    }
    return <DefaultMessage />;
  };

  return <div className={styles.lowerRow}>{renderMessage()}</div>;
};

const KaggleBanner = ({
  bannerRef,
  closeBanner,
  newDatasetId,
}: KaggleBannerProps) => {
  const [kaggleInputText, setKaggleInputText] = useState("");
  const [kaggleInputError, setKaggleInputError] = useState("");

  const dispatch = useDispatch();

  const isSubmitBtnEnabled = !!kaggleInputText && !kaggleInputError;

  const dynamicBorders = {
    [styles.errorBorder]: !!kaggleInputError,
  };

  const kaggleBannerClassNames = classNames(styles.kaggleBanner, {
    ...dynamicBorders,
  });

  const kaggleInputContainerClassNames = classNames(
    styles.kaggleInputContainer,
    {
      ...dynamicBorders,
    },
  );

  const clearInputButtonClassNames = classNames(styles.clearInputButton, {
    [styles.hiddenDiv]: !kaggleInputText,
  });

  const clearInputText = () => {
    setKaggleInputText("");
    setKaggleInputError("");
  };

  const validateKaggleInput = (value: string): string => {
    // Remove any leading/trailing whitespace
    const trimmedValue = value.trim();

    if (!trimmedValue) {
      return "";
    }

    // Check if it's a full Kaggle URL
    const kaggleUrlPattern =
      /^https?:\/\/(www\.)?kaggle\.com\/datasets\/([^/]+)\/([^/]+)/i;
    const urlMatch = trimmedValue.match(kaggleUrlPattern);

    if (urlMatch) {
      // Extract owner/dataset from URL
      return `${urlMatch[2]}/${urlMatch[3]}`;
    }

    // Check if it's already in OWNER/DATASET format
    const ownerDatasetPattern = /^[a-zA-Z0-9-]+\/[a-zA-Z0-9-]+$/;
    if (ownerDatasetPattern.test(trimmedValue)) {
      return trimmedValue;
    }

    // Invalid format
    return "";
  };

  const handleInputValidation = (value: string) => {
    const validatedValue = validateKaggleInput(value);

    if (validatedValue) {
      setKaggleInputError("");
      return validatedValue;
    } else if (value) {
      setKaggleInputError(
        "Error: Please use OWNER/DATASET format or a valid Kaggle URL",
      );
    } else {
      setKaggleInputError("");
    }
    return "";
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setKaggleInputText(value);
    handleInputValidation(value);
  };

  const handleSaveKaggleDataset = async () => {
    if (newDatasetId) {
      const validatedValue = validateKaggleInput(kaggleInputText);
      if (validatedValue) {
        // Mark connect step as complete
        dispatch(setConnectStatus(StepTileProgressStatus.COMPLETE));

        // Enable annotations step
        dispatch(setAnnotationStatus(StepTileProgressStatus.ACTIVE));

        // Set the data source as Kaggle
        dispatch(setPreviewDataSource(DataSources.KAGGLE));

        // For Kaggle, we don't need to upload files, so we mark upload as already finished
        // This way when annotations are done, it will trigger the backend API call
        dispatch(setUploadStatus(StepTileProgressStatus.ACTIVE));
        dispatch(setUploadFinished(true));

        // Store the Kaggle resource for later use
        sessionStorage.setItem("kaggle_resource", validatedValue);

        closeBanner();
      }
    }
  };

  const renderBody = () => {
    return (
      <div className={styles.kaggleContainer}>
        <div className={styles.kaggleHeader}>
          <ArrowIcon />
          <span>Kaggle Dataset</span>
          <div className={styles.cancelBtn} onClick={closeBanner}>
            <XIcon />
          </div>
        </div>
        <div className={styles.kaggleBody}>
          <div className={styles.upperRow}>
            <div className={kaggleInputContainerClassNames}>
              <input
                type="text"
                placeholder="e.g., titanic/titanic or https://www.kaggle.com/datasets/titanic/titanic"
                className={styles.kaggleInput}
                onChange={handleInputChange}
                value={kaggleInputText}
              />
              <div
                className={clearInputButtonClassNames}
                onClick={clearInputText}
              >
                <XIcon />
              </div>
            </div>
          </div>
          <KaggleBannerMessage kaggleInputError={kaggleInputError} />
        </div>
      </div>
    );
  };

  return (
    <div className={kaggleBannerClassNames}>
      <InputBanner
        body={renderBody()}
        bannerRef={bannerRef}
        isSaveBtnEnabled={isSubmitBtnEnabled}
        handleSaveButtonClick={handleSaveKaggleDataset}
        closeBanner={closeBanner}
      />
    </div>
  );
};

const mapStateToProps = (state: State) => {
  return {
    newDatasetId: getNewDatasetId(state),
  };
};

export default connect(mapStateToProps)(KaggleBanner);

interface KaggleBannerProps {
  bannerRef: RefObject<HTMLDivElement>;
  closeBanner: () => void;
  newDatasetId: string | null;
}

interface KaggleBannerMessageProps {
  kaggleInputError: string;
}
